import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { fetchMe, activateCreator, creatorSimulatePay, creatorSetLock, creatorReset } from "@/lib/me";
import { API, getSessionId } from "@/lib/api";

const SCREENS = [
    ["Intro", "/"],
    ["Questionnaire", "/analyse"],
    ["Scan visage", "/photo"],
    ["Scan corps", "/photo-corps"],
    ["Chargement", "/analyse-en-cours"],
    ["Aperçu / Offre", "/apercu"],
    ["Accueil", "/accueil"],
    ["Coach", "/coach"],
    ["Compte", "/compte"],
    ["Résiliation", "/compte?retention=1"],
];

// Panneau créateur. Activé par le lien secret, puis accessible via /createur.
const Creator = () => {
    const navigate = useNavigate();
    const { token } = useParams();
    const [me, setMe] = useState(null);
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = async () => setMe(await fetchMe());

    // Garantit qu'une session existe avant d'activer le mode créateur (évite session_id=null au 1er chargement).
    const ensureSession = async () => {
        let sid = getSessionId();
        if (sid) return sid;
        try {
            const { data } = await axios.post(`${API}/session`, { source: "createur", landing_path: "/createur" });
            sid = data.session_id;
            localStorage.setItem("revanche_session_id", sid);
        } catch (e) { /* noop */ }
        return sid;
    };

    useEffect(() => {
        (async () => {
            if (token) {
                await ensureSession();
                try { await activateCreator(token); }
                catch (e) { setMsg("Lien créateur invalide."); }
                finally { window.history.replaceState({}, "", "/createur"); }
            }
            await refresh();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const act = async (fn, ok) => {
        if (busy) return; setBusy(true); setMsg("");
        try { const r = await fn(); if (r?.session_id) localStorage.setItem("revanche_session_id", r.session_id); setMsg(ok); await refresh(); }
        catch (e) { setMsg("Action impossible."); }
        finally { setBusy(false); }
    };

    if (me && !me.mode_createur && !token) {
        return (
            <div className="fixed inset-0 bg-black flex items-center justify-center px-6 text-center" data-testid="creator-denied">
                <p className="text-white">Mode créateur non actif sur cet appareil. Ouvre le lien secret.</p>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black max-w-[480px] mx-auto overflow-y-auto scrollbar-none px-5 py-6 safe-top safe-bottom" data-testid="creator-screen">
            <h1 className="font-display font-black uppercase text-white text-3xl">Mode créateur</h1>
            {msg && <p className="mt-2 text-sm text-revred" data-testid="creator-msg">{msg}</p>}

            <div className="mt-4 rounded-2xl p-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                <p className="text-sm text-white/85">
                    Abonné : <b className="text-white">{me?.abonne ? "oui" : "non"}</b> · Crédits : <b className="text-white">{me?.credits ?? 0}</b> ·
                    Questionnaire : <b className="text-white">{me?.questionnaire_termine ? "fini" : "en cours"}</b>
                </p>
                <p className="text-[11px] text-revtext-secondary mt-1 break-all">session : {getSessionId()}</p>
            </div>

            <p className="font-display font-black uppercase text-white text-lg mt-6 mb-2">Contrôles</p>
            <div className="grid grid-cols-2 gap-2">
                <button data-testid="creator-simulate-pay" disabled={busy} onClick={() => act(creatorSimulatePay, "Paiement simulé, tout débloqué + 500 crédits.")}
                    className="tap-feedback h-12 rounded-xl bg-revred text-white font-semibold">Paiement simulé</button>
                <button data-testid="creator-unlock" disabled={busy} onClick={() => act(() => creatorSetLock(false), "Débloqué.")}
                    className="tap-feedback h-12 rounded-xl text-white" style={{ border: "1px solid var(--border)" }}>Débloquer</button>
                <button data-testid="creator-lock" disabled={busy} onClick={() => act(() => creatorSetLock(true), "Verrouillé.")}
                    className="tap-feedback h-12 rounded-xl text-white" style={{ border: "1px solid var(--border)" }}>Verrouiller</button>
                <button data-testid="creator-reset" disabled={busy} onClick={() => act(creatorReset, "Réinitialisé comme un nouvel utilisateur.")}
                    className="tap-feedback h-12 rounded-xl text-revred font-semibold" style={{ border: "1px solid var(--red)" }}>Tout réinitialiser</button>
            </div>

            <p className="font-display font-black uppercase text-white text-lg mt-6 mb-2">Aller à un écran</p>
            <div className="grid grid-cols-2 gap-2 pb-4">
                {SCREENS.map(([label, path]) => (
                    <button key={path} data-testid={`creator-go-${path}`} onClick={() => navigate(path)}
                        className="tap-feedback h-11 rounded-xl text-white text-sm" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default Creator;
